import React, {useEffect, useRef} from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
    Box, Typography, Link, Chip, Alert, Paper, Divider, Checkbox, FormControlLabel
} from "@mui/material";
import {
    OpenInNew as OpenInNewIcon,
    Place as PlaceIcon,
    AccountTree as HierarchyIcon,
    Upload as UploadIcon,
    Edit as EditIcon,
    Info as InfoIcon,
    Visibility as VisibilityIcon
} from "@mui/icons-material";

const LocationNameDialog = ({
    open,
    place,
    originalPlaceName,
    modifiedPlaceName,
    showEmptyInputError,
    selectedLanguage,
    customLanguageObject,
    onClose,
    onSave,
    onPlaceNameChange,
    onShowHierarchy,
    onMarkAsSeen,
    onMarkAsCorrect
}) => {
    const inputRef = useRef(null);
    const [markAsSeen, setMarkAsSeen] = React.useState(false);
    const [markAsCorrect, setMarkAsCorrect] = React.useState(false);

    useEffect(() => {
        if (open) {
            // Reset the checkboxes when dialog opens
            setMarkAsSeen(false);
            setMarkAsCorrect(false);
            // Delay focusing until after the dialog fully opens
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
        }
    }, [open]);

    const handleMarkAsSeenChange = (event) => {
        setMarkAsSeen(event.target.checked);
        if (event.target.checked) {
            setMarkAsCorrect(false); // Can't be both
        }
        // Don't mark as seen immediately - let the save button handle it
    };

    const handleMarkAsCorrectChange = (event) => {
        setMarkAsCorrect(event.target.checked);
        if (event.target.checked) {
            setMarkAsSeen(false); // Can't be both
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onSave();
        }
    };

    const getLanguageDisplayText = () => {
        if (customLanguageObject) {
            return `${customLanguageObject.customLabel || ''} (${customLanguageObject.customLanguageCode || ''})`;
        }
        if (selectedLanguage) {
            return `${selectedLanguage.wikidataLabel || ''}${selectedLanguage.nativeLabels ? ` (${selectedLanguage.nativeLabels})` : ''}`;
        }
        return '';
    };

    const getWikidataUrl = () => {
        if (!place?.wikidataID) return null;
        // Extract just the Q-ID from the full Wikidata URL
        const qId = place.wikidataID.replace('http://www.wikidata.org/entity/', '');
        return `https://www.wikidata.org/wiki/${qId}`;
    };

    const getWikidataId = () => {
        if (!place?.wikidataID) return null;
        return place.wikidataID.replace('http://www.wikidata.org/entity/', '');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            disableEnforceFocus
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PlaceIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="h5" component="div" sx={{ 
                        fontWeight: 600,
                        color: place?.isUnnamedPlace() ? 'error.main' : 'text.primary',
                        fontStyle: place?.isUnnamedPlace() ? 'italic' : 'normal'
                    }}>
                        {place?.getDisplayName() || originalPlaceName || 'Unnamed Place'}
                    </Typography>
                    {place?.isUnnamedPlace() && (
                        <Chip
                            icon={<span>⚠️</span>}
                            label="No Name"
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                        />
                    )}
                </Box>
                
                {/* Wikidata Link */}
                {getWikidataUrl() && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Chip
                            icon={<OpenInNewIcon sx={{ fontSize: '16px !important' }} />}
                            label={`Wikidata: ${getWikidataId()}`}
                            component={Link}
                            href={getWikidataUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            clickable
                            size="small"
                            variant="outlined"
                            sx={{
                                fontSize: '0.75rem',
                                '&:hover': {
                                    backgroundColor: 'primary.light',
                                    color: 'white'
                                },
                                textDecoration: 'none'
                            }}
                        />
                        {place?.lat && place?.lon && (
                            <Typography variant="caption" color="text.secondary">
                                📍 {place.lat.toFixed(4)}, {place.lon.toFixed(4)}
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogTitle>
            
            <DialogContent sx={{ pt: 2 }}>
                {place?.isUnnamedPlace() && (
                    <Alert 
                        severity="info" 
                        sx={{ mb: 2, borderRadius: 2 }}
                        icon={<span style={{ fontSize: '1.1rem' }}>ℹ️</span>}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                            This place has no name in Wikidata yet
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', opacity: 0.9 }}>
                            You're seeing only the Wikidata identifier (Q-ID) because no proper name has been added to this entry. 
                            Adding an endonym here will help provide valuable naming information for this location.
                        </Typography>
                    </Alert>
                )}
                
                <TextField
                    label="Selected Language"
                    value={getLanguageDisplayText()}
                    fullWidth
                    disabled
                    margin="normal"
                    size="small"
                />

                {/* Existing Label Warning */}
                {place?.hasExistingLabel() && (
                    <Alert 
                        severity="warning" 
                        sx={{ mt: 2, mb: 2, borderRadius: 2 }}
                        icon={<span style={{ fontSize: '1.1rem' }}>🏷️</span>}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            This place already has a label in {getLanguageDisplayText()}
                        </Typography>
                        <Box sx={{ 
                            backgroundColor: 'rgba(255, 255, 255, 0.7)', 
                            p: 1.5, 
                            borderRadius: 1, 
                            border: '1px solid rgba(0, 0, 0, 0.1)',
                            mb: 1
                        }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                Current label: "{place.existingLabel}"
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', opacity: 0.9 }}>
                            ⚠️ <strong>Important:</strong> If you enter a new name below, it will <strong>replace</strong> the current label when uploaded to Wikidata.
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', opacity: 0.9, mt: 0.5 }}>
                            If the current label is correct, you can mark it as correct below to remove this pin from the map.
                        </Typography>
                    </Alert>
                )}

                {/* Information about contribution options */}
                <Paper sx={{ 
                    p: 2, 
                    mt: 2, 
                    mb: 2, 
                    backgroundColor: 'rgba(59, 130, 246, 0.05)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <InfoIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            How to contribute place names
                        </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                        You have two options to contribute the local name for this place:
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <UploadIcon sx={{ color: 'primary.main', fontSize: 18, mt: 0.2 }} />
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                    Option 1: Add name here and upload later
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                    Enter the name below and save it to your local collection. Later, you can upload all your contributions to Wikidata at once using an API key (requires Wikidata account).
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic', mt: 0.5 }}>
                                    💡 This is also useful for personal documentation and research, even if you don't plan to upload to Wikidata.
                                </Typography>
                            </Box>
                        </Box>
                        
                        <Divider sx={{ my: 0.5 }} />
                        
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <EditIcon sx={{ color: 'secondary.main', fontSize: 18, mt: 0.2 }} />
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                    Option 2: Edit directly on Wikidata
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                        Click the Wikidata link above to edit this place directly on Wikidata.
                                    </Typography>
                                    {getWikidataUrl() && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Link
                                                href={getWikidataUrl()}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    fontSize: '0.8rem',
                                                    fontWeight: 500,
                                                    textDecoration: 'none',
                                                    color: 'secondary.main',
                                                    '&:hover': {
                                                        textDecoration: 'underline'
                                                    }
                                                }}
                                            >
                                                Edit {getWikidataId()} on Wikidata
                                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                                            </Link>
                                            
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={markAsSeen}
                                                        onChange={handleMarkAsSeenChange}
                                                        size="small"
                                                        sx={{ 
                                                            color: 'secondary.main',
                                                            '&.Mui-checked': {
                                                                color: 'secondary.main',
                                                            }
                                                        }}
                                                    />
                                                }
                                                label={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <VisibilityIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                                                        <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                                            I added the name directly on Wikidata (skip name input below)
                                                        </Typography>
                                                    </Box>
                                                }
                                                sx={{ 
                                                    ml: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.75rem'
                                                    }
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                        
                        {/* Mark as Correct Option - only show if place has existing label */}
                        {place?.hasExistingLabel() && (
                            <>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <span style={{ fontSize: '18px', marginTop: '2px' }}>✅</span>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                            Option 3: Mark current label as correct
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', mb: 1 }}>
                                            If the existing label "{place.existingLabel}" is already correct, you can mark it as such to remove this pin from the map.
                                        </Typography>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={markAsCorrect}
                                                    onChange={handleMarkAsCorrectChange}
                                                    size="small"
                                                    sx={{ 
                                                        color: 'success.main',
                                                        '&.Mui-checked': {
                                                            color: 'success.main',
                                                        }
                                                    }}
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <span style={{ fontSize: '14px' }}>✅</span>
                                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                                        The current label is correct (this will hide the pin)
                                                    </Typography>
                                                </Box>
                                            }
                                            sx={{ 
                                                ml: 0,
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: '0.75rem'
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                </Paper>

                <TextField
                    label="Name of this place in your language"
                    value={modifiedPlaceName}
                    onChange={onPlaceNameChange}
                    onKeyDown={handleKeyDown}
                    fullWidth
                    margin="normal"
                    placeholder={markAsSeen 
                        ? "Name entered on Wikidata (optional here)" 
                        : markAsCorrect
                            ? "Label marked as correct (input disabled)"
                            : place?.isUnnamedPlace() 
                                ? "Enter the name for this unnamed place" 
                                : "Enter the local name for this place"
                    }
                    disabled={markAsSeen || markAsCorrect}
                    error={showEmptyInputError && !modifiedPlaceName.trim() && !markAsSeen && !markAsCorrect}
                    helperText={markAsSeen 
                        ? "✓ You indicated the name was added directly on Wikidata" 
                        : markAsCorrect
                            ? "✅ You marked the current label as correct"
                            : showEmptyInputError && !modifiedPlaceName.trim() 
                                ? "Please enter a name or check one of the boxes above" 
                                : place?.hasExistingLabel()
                                    ? "⚠️ This will replace the existing label when uploaded to Wikidata"
                                    : place?.isUnnamedPlace()
                                        ? "What do locals call this place? Your input is especially valuable here!"
                                        : "How do people in your language call this place?"
                    }
                    inputRef={inputRef}
                    sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                            color: 'text.secondary',
                            WebkitTextFillColor: 'text.secondary'
                        }
                    }}
                />
            </DialogContent>
            
            <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button 
                    onClick={() => onShowHierarchy && onShowHierarchy(place)}
                    variant="outlined"
                    startIcon={<HierarchyIcon />}
                    sx={{ borderRadius: 2, mr: 'auto' }}
                    disabled={!place}
                >
                    Show Hierarchy Tree
                </Button>
                <Button 
                    onClick={onClose}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={() => {
                        if (markAsSeen && onMarkAsSeen) {
                            onMarkAsSeen(place);
                        } else if (markAsCorrect && onMarkAsCorrect) {
                            onMarkAsCorrect(place);
                        } else {
                            onSave();
                        }
                    }}
                    variant="contained"
                    sx={{ borderRadius: 2, px: 3 }}
                >
                    {markAsSeen ? 'Mark as Completed' : markAsCorrect ? 'Mark as Correct' : 'Save Endonym'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LocationNameDialog;